import { mapToCamelCase } from '@consigli/utils';
import { z } from 'zod';
import { documentPackageSchema } from './document-package';
import { FoldersDocumentType } from './folders-document-type';
import { NS3451 } from './ns3451';
import { OMUK } from './om-uk';
import { getPaginationResponseSchema } from './pagination';
import { projectSchema } from './project';
export var BlobUpdatedBy;
(function (BlobUpdatedBy) {
    BlobUpdatedBy["PROCESSOR"] = "PROCESSOR";
    BlobUpdatedBy["CONSIGLI"] = "CONSIGLI";
    BlobUpdatedBy["CUSTOMER"] = "CUSTOMER";
})(BlobUpdatedBy || (BlobUpdatedBy = {}));
export var VersionCompareRole;
(function (VersionCompareRole) {
    VersionCompareRole["PRIMARY_VERSION"] = "PRIMARY_VERSION";
    VersionCompareRole["SECONDARY_VERSION"] = "SECONDARY_VERSION";
    VersionCompareRole["COMMENT_VERSION"] = "COMMENT_VERSION";
})(VersionCompareRole || (VersionCompareRole = {}));
export var BlobParsedStatus;
(function (BlobParsedStatus) {
    BlobParsedStatus["PENDING"] = "PENDING";
    BlobParsedStatus["UPLOADING"] = "UPLOADING";
    BlobParsedStatus["UPLOADED"] = "UPLOADED";
    BlobParsedStatus["UPLOAD_FAILED"] = "UPLOAD_FAILED";
    BlobParsedStatus["UNPARSED"] = "UNPARSED";
    BlobParsedStatus["PARSED"] = "PARSED";
    BlobParsedStatus["PARSING_FAILED"] = "PARSING_FAILED";
    BlobParsedStatus["DUPLICATE"] = "DUPLICATE";
})(BlobParsedStatus || (BlobParsedStatus = {}));
export var ScheduleCheckFileType;
(function (ScheduleCheckFileType) {
    ScheduleCheckFileType["SCHEDULE_FILE"] = "SCHEDULE_FILE";
    ScheduleCheckFileType["DRAWINGS"] = "DRAWINGS";
    ScheduleCheckFileType["SCHEMATIC_DRAWINGS"] = "SCHEMATIC_DRAWINGS";
})(ScheduleCheckFileType || (ScheduleCheckFileType = {}));
export var WaterProcessFileType;
(function (WaterProcessFileType) {
    WaterProcessFileType["SCHEDULE"] = "SCHEDULE";
    WaterProcessFileType["PID"] = "PID";
    WaterProcessFileType["SPECS"] = "SPECS";
})(WaterProcessFileType || (WaterProcessFileType = {}));
export const blobParsedStatusSchema = z.nativeEnum(BlobParsedStatus);
export const scheduleCheckFileTypeSchema = z.nativeEnum(ScheduleCheckFileType);
export const blobMetadataSchema = z
    .object({
    is_sample_report: z.boolean().optional(),
    water_process_file_type: z.nativeEnum(WaterProcessFileType).optional(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const blobParsedDataSchema = z
    .object({
    remaining_occurrences: z.number().int().optional(),
    data: z
        .array(z.object({
        id: z.number().int(),
        content: z.string(),
    }))
        .optional(),
})
    .strict()
    .transform(mapToCamelCase);
const enumFolderStructureSchema = z.union([z.nativeEnum(OMUK), z.nativeEnum(NS3451)]);
export const blobSchema = z
    .object({
    id: z.string().uuid(),
    project: projectSchema.innerType().shape.id,
    document_package: documentPackageSchema.innerType().shape.id,
    md5: z.string().nullable(),
    name: z.string(),
    name_suggestion: z.string().nullable(),
    extension: z.string(),
    parsed_status: blobParsedStatusSchema,
    file_url: z.string(),
    download_url: z.string(),
    parsed_data: blobParsedDataSchema,
    is_drawing_index_list: z.boolean(),
    is_checklist: z.boolean(),
    schedule_check_file_type: scheduleCheckFileTypeSchema.nullable().optional(),
    original_file_id: z.string().uuid(),
    converted_pdf_file_id: z.string().uuid().nullable(),
    ocr_pdf_file_id: z.string().uuid().nullable(),
    ns3451: z.array(z.nativeEnum(NS3451)),
    document_type: z.array(z.nativeEnum(FoldersDocumentType)),
    labels_reused: z.boolean(),
    external_famac_file_id: z.string().nullable(),
    folder_structure: z.array(enumFolderStructureSchema),
    metadata: blobMetadataSchema.optional(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobsResponseSchema = getPaginationResponseSchema(blobSchema);
export const getBlobsRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    page: z.union([z.number().int(), z.literal('all')]),
    page_size: z.number().int().nullish(),
    search: z.string().nullish(),
    search_parsed_data: z.string().nullish(),
    status: z.array(z.nativeEnum(BlobParsedStatus)).nullish(),
    suggestion: z.boolean().nullish(),
    standard: z.string().nullish(),
    document_type: z.array(z.nativeEnum(FoldersDocumentType)).nullish(),
    blob_ids: z.array(z.string().uuid()).nullish(),
    labels_reused: z.boolean().nullish(),
    // Bid Review: scope to a single role via blob `metadata.reference`
    // (`primary_reference`, a contractor name, `bid_levelling`, or `__other__`).
    reference: z.string().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const createBlobSchema = blobSchema
    .innerType()
    .pick({
    document_package: true, // TODO: Remove once Blob is nested under packages in API
    name: true,
    parsed_status: true,
    original_file_id: true,
    is_drawing_index_list: true,
    is_checklist: true, // it has to be one word checklist because in backend it is is_checklist
    schedule_check_file_type: true,
    metadata: true,
})
    .required()
    .partial({
    schedule_check_file_type: true,
    metadata: true,
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobReadUrlResponseSchema = z
    .object({
    read_url: z.string(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobUploadUrlResponseSchema = z
    .object({
    upload_url: z.string(),
})
    .strict()
    .transform(mapToCamelCase);
export const updateBatchBlobsResponseSchema = z.array(blobSchema).transform(mapToCamelCase);
export const blobFilterCountSchema = z
    .object({
    parsed: z.number().int(),
    unparsed: z.number().int(),
    duplicate: z.number().int(),
    corrupted: z.number().int(),
    suggestion: z.number().int(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobFilterCountRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    search_parsed_data: z.string().nullish(),
    blob_ids: z.array(z.string().uuid()).nullish(),
    search: z.string().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobNSCountRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    document_type: z.array(z.nativeEnum(FoldersDocumentType)),
    search_parsed_data: z.string().nullable(),
    blob_ids: z.array(z.string().uuid()).nullish(),
    search: z.string().nullable(),
    status: z.array(z.nativeEnum(BlobParsedStatus)).nullish(),
    labels_reused: z.boolean().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobNSCountResponseSchema = z.record(z.number());
export const getBlobUniqueDocumentTypesRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    standard: z.string().nullish(),
    search_parsed_data: z.string().nullish(),
    documentType: z.array(z.nativeEnum(FoldersDocumentType)).nullish(),
    blob_ids: z.array(z.string().uuid()).nullish(),
    search: z.string().nullish(),
    status: z.array(z.nativeEnum(BlobParsedStatus)).nullish(),
    labels_reused: z.boolean().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobUniqueDocumentTypesResponseSchema = z.array(z.nativeEnum(FoldersDocumentType));
export const blobHistorySchema = z
    .object({
    blob_id: z.string().uuid(),
    blob_name: z.string(),
    ns3451: z.object({
        from: z.object({
            value: z.array(z.nativeEnum(NS3451)),
            created_by: z.nativeEnum(BlobUpdatedBy).nullable(),
        }),
        to: z.object({
            value: z.array(z.nativeEnum(NS3451)),
            created_by: z.nativeEnum(BlobUpdatedBy).nullable(),
        }),
    }),
    document_type: z.object({
        from: z.object({
            value: z.array(z.nativeEnum(FoldersDocumentType)),
            created_by: z.nativeEnum(BlobUpdatedBy).nullable(),
        }),
        to: z.object({
            value: z.array(z.nativeEnum(FoldersDocumentType)),
            created_by: z.nativeEnum(BlobUpdatedBy).nullable(),
        }),
    }),
    folder_structure: z.object({
        from: z.object({
            value: z.array(enumFolderStructureSchema),
            created_by: z.nativeEnum(BlobUpdatedBy).nullable(),
        }),
        to: z.object({
            value: z.array(enumFolderStructureSchema),
            created_by: z.nativeEnum(BlobUpdatedBy).nullable(),
        }),
    }),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobHistoryResponseSchema = getPaginationResponseSchema(blobHistorySchema);
export const getBlobHistoryRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    page: z.union([z.number().int(), z.literal('all')]),
})
    .strict()
    .transform(mapToCamelCase);
export const blobNameListSchema = z.array(z
    .object({
    id: z.string().uuid(),
    name: z.string(),
})
    .strict()
    .transform(mapToCamelCase));
export const getBlobNameListRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    search_parsed_data: z.string().nullish(),
    status: z.array(z.nativeEnum(BlobParsedStatus)).nullish(),
    document_type: z.array(z.nativeEnum(FoldersDocumentType)).nullish(),
    standard: z.string().nullish(),
})
    .strict()
    .transform(mapToCamelCase);
export const getBlobContractorsResponseSchema = z.array(z.string());
export const getBlobContractorsRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
})
    .strict()
    .transform(mapToCamelCase);
// Sentinel the blobs list/count use to select documents with no reference tag.
export const OTHER_REFERENCE = '__other__';
export const getBlobReferenceCountResponseSchema = z.record(z.number());
export const getBlobReferenceCountRequestSchema = z
    .object({
    project_id: z.number().int(),
    package_id: z.string().uuid(),
    status: z.array(z.nativeEnum(BlobParsedStatus)).nullish(),
})
    .strict()
    .transform(mapToCamelCase);
